import type { Organization, Subscription, User } from '@witzy/shared';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Auth store — the single source of truth for "who is logged in".
 * Tokens persist to localStorage so a reload keeps the session. Real wiring
 * to `/api/v1/auth/*` arrives in P1·S5.
 */
export interface AuthState {
  user: User | null;
  organization: Organization | null;
  subscription: Subscription | null;
  token: string | null;
  refreshToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * Email of the super-admin who opened this session from the admin console
   * ("Impersonate"), or null for a normal login. Lets routing send a support
   * session into the app instead of to /login when it meets an admin-only
   * route.
   */
  impersonatedBy: string | null;

  setAuth: (a: {
    user: User;
    organization: Organization | null;
    subscription: Subscription | null;
    token: string;
    refreshToken: string;
    impersonatedBy?: string | null;
  }) => void;
  clearAuth: () => void;
  updateUser: (partial: Partial<User>) => void;
  updateOrganization: (partial: Partial<Organization>) => void;
  setLoading: (loading: boolean) => void;

  /** Dev-only helper — seeds a fake session so guarded routes can be opened
   *  without a running backend. Replace with the real login flow in P1·S5. */
  devLogin: (role?: 'business_admin' | 'super_admin') => void;
}

const DEV_USER: User = {
  id: '00000000-0000-0000-0000-000000000001',
  email: 'admin@witzy.dev',
  first_name: 'Dev',
  last_name: 'Admin',
  avatar_url: null,
  phone: null,
  role: 'business_admin',
  is_active: true,
  is_email_verified: true,
  organization_id: '00000000-0000-0000-0000-0000000000aa',
  created_at: new Date().toISOString(),
};

const DEV_ORG: Organization = {
  id: '00000000-0000-0000-0000-0000000000aa',
  name: 'Witzy Dev Co.',
  company_name: 'Witzy Dev Co.',
  admin_email: 'admin@witzy.dev',
  admin_phone: null,
  logo_url: null,
  industry: 'Software',
  website: null,
  timezone: 'Asia/Calcutta',
  currency: 'INR',
  notification_email: null,
  billing_name: null,
  billing_company_legal_name: null,
  billing_gstin: null,
  billing_address_line1: null,
  billing_address_line2: null,
  billing_city: null,
  billing_state_code: null,
  billing_postal_code: null,
  billing_country_code: null,
  invoice_org_code: null,
  onboarding_status: 'active',
  is_onboarding_complete: true,
  ai_free_quota_override: null,
  aisensy_business_id: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      organization: null,
      subscription: null,
      token: null,
      refreshToken: null,
      isLoading: false,
      isAuthenticated: false,
      impersonatedBy: null,

      setAuth: ({ user, organization, subscription, token, refreshToken, impersonatedBy }) =>
        set((cur) => ({
          user,
          organization,
          subscription,
          token,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
          // A token refresh re-calls setAuth without the flag — keep it for the
          // same user; any other login replaces it.
          impersonatedBy:
            impersonatedBy !== undefined
              ? impersonatedBy
              : cur.user?.id === user.id
                ? cur.impersonatedBy
                : null,
        })),

      clearAuth: () =>
        set({
          user: null,
          organization: null,
          subscription: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
          impersonatedBy: null,
        }),

      updateUser: (partial) => {
        const cur = get().user;
        if (!cur) return;
        set({ user: { ...cur, ...partial } });
      },

      updateOrganization: (partial) => {
        const cur = get().organization;
        if (!cur) return;
        set({ organization: { ...cur, ...partial } });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      devLogin: (role = 'business_admin') =>
        set({
          user: { ...DEV_USER, role, organization_id: role === 'super_admin' ? null : DEV_USER.organization_id },
          organization: role === 'super_admin' ? null : DEV_ORG,
          subscription: null,
          token: 'dev-mock-access-token',
          refreshToken: 'dev-mock-refresh-token',
          isAuthenticated: true,
          isLoading: false,
        }),
    }),
    {
      name: 'witzy-auth',
      partialize: (s) => ({
        user: s.user,
        organization: s.organization,
        subscription: s.subscription,
        token: s.token,
        refreshToken: s.refreshToken,
        isAuthenticated: s.isAuthenticated,
        impersonatedBy: s.impersonatedBy,
      }),
    },
  ),
);
